<template>
    <div class="d-md-flex mt_26 cost_total_col align-items-start">
        <div class="quantity d-flex align-items-center">
            <h4 class="font_size_22 mb-0">Quantity</h4>
            <h3 class="font_size_35 mb-0 ml_10">{{ quantity }}</h3>
        </div>

        <div class="cost-breakdown-container ml-auto">
            <div class="cost-row">
                <div class="tooltip-wrapper" @click.stop="toggleTooltip">
                    <span class="cost-label">Base Fee</span>
                    <svg
                        class="info-icon-svg"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clip-rule="evenodd"
                        />
                    </svg>

                    <div
                        v-if="baseCost > 0 || otherServices.length > 0"
                        class="tooltip-content"
                        :class="{ 'is-visible': tooltipVisible }"
                    >
                        <div class="tooltip-header">
                            <h4>Base Fee Breakdown</h4>
                        </div>
                        <div class="tooltip-body">
                            <table class="base-fee-table">
                                <thead>
                                    <tr>
                                        <th>Fee</th>
                                        <th>Color</th>
                                        <th>Qty</th>
                                        <th>Amt</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <!-- Color-Size Combinations -->
                                    <template
                                        v-if="
                                            colorSizeCombinations &&
                                            colorSizeCombinations.length > 0
                                        "
                                    >
                                        <tr
                                            v-for="(
                                                combination, index
                                            ) in colorSizeCombinations"
                                            :key="'color-size-' + index"
                                        >
                                            <td>
                                                Product Base Price -
                                                {{ combination.size || "N/A" }}
                                            </td>
                                            <td>
                                                <div class="color-box-wrapper">
                                                    <div
                                                        class="color-box"
                                                        :style="{
                                                            backgroundColor:
                                                                getColorValue(
                                                                    combination
                                                                )
                                                        }"
                                                        :title="
                                                            getColorName(
                                                                combination
                                                            )
                                                        "
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                {{ combination.quantity || 0 }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getColorSizeUnitPrice(
                                                            combination
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getColorSizeTotalPrice(
                                                            combination
                                                        )
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Services grouped by Rollup Fee (rollup_fee_id = 1 for Base Fee) -->
                                    <template
                                        v-for="rollupGroup in getServicesGroupedByRollupFee(
                                            1
                                        )"
                                    >
                                        <!-- Services included in Product Base Price (show_service = false) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === false;
                                                }
                                            )"
                                            :key="service.id"
                                            class="disable_data"
                                        >
                                            <td>
                                                {{ getServiceName(service) }}
                                            </td>
                                            <td>1</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>1</td>
                                            <td>INCL</td>
                                            <td>INCL</td>
                                        </tr>

                                        <!-- Services shown as separate lines (show_service = true) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === true;
                                                }
                                            )"
                                            :key="service.id"
                                        >
                                            <td>
                                                {{ getServiceName(service) }}
                                            </td>
                                            <td>-</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Product Base Price (Total) - shown at end after services -->
                                    <tr
                                        v-if="baseCost > 0"
                                        class="product-base-price-row d-none"
                                    >
                                        <td>
                                            <strong>Product Base Price</strong>
                                        </td>
                                        <td>-</td>
                                        <td>
                                            <strong>{{ quantity }}</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(getBaseUnitPrice())
                                            }}</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(
                                                    getProductBasePriceTotal()
                                                )
                                            }}</strong>
                                        </td>
                                    </tr>

                                    <!-- Total Base Price -->
                                    <tr class="total-row">
                                        <td colspan="4">
                                            <strong>Total</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(displayBaseCost)
                                            }}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <span class="cost-value">{{
                    formatPrice(displayBaseCost)
                }}</span>
            </div>
            <div class="cost-row" v-if="calculatedRunChargeTotal > 0">
                <div
                    class="tooltip-wrapper"
                    @click.stop="toggleRunChargeTooltip"
                    v-if="hasRunChargeData()"
                >
                    <span class="cost-label">Run Charge</span>
                    <svg
                        class="info-icon-svg"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <div
                        class="tooltip-content"
                        :class="{ 'is-visible': runChargeTooltipVisible }"
                    >
                        <div class="tooltip-header">
                            <h4>Run Charge Breakdown</h4>
                        </div>
                        <div class="tooltip-body">
                            <table class="base-fee-table">
                                <thead>
                                    <tr>
                                        <th>Fee</th>
                                        <th>Qty</th>
                                        <th>Amt</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <!-- 1st Location -->
                                    <tr v-if="getFirstLocationData()">
                                        <td>
                                            <strong
                                                >1st Location{{
                                                    getFirstLocationName() !==
                                                    "1st Location"
                                                        ? " (" +
                                                          getFirstLocationName() +
                                                          ")"
                                                        : ""
                                                }}</strong
                                            >
                                        </td>
                                        <td>{{ quantity }}</td>
                                        <td
                                            v-if="
                                                getFirstLocationBaseCost() === 0
                                            "
                                        >
                                            INCL
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getFirstLocationBaseCost()
                                                )
                                            }}
                                        </td>
                                        <td
                                            v-if="
                                                getFirstLocationBaseCost() === 0
                                            "
                                        >
                                            INCL
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getFirstLocationBaseCost() *
                                                        quantity
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- 1st Color or Full Color -->
                                    <tr v-if="getFirstLocationData()">
                                        <td>
                                            <span
                                                v-if="
                                                    isFirstLocationFullColor()
                                                "
                                                >Full Color</span
                                            >
                                            <span v-else>1st Color</span>
                                        </td>
                                        <td>{{ quantity }}</td>
                                        <td v-if="getFirstColorCost() === 0">
                                            INCL
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(getFirstColorCost())
                                            }}
                                        </td>
                                        <td v-if="getFirstColorCost() === 0">
                                            INCL
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getFirstColorCost() *
                                                        quantity
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Addl Color for Location 1 (if location1Colors > 1) -->
                                    <tr
                                        v-if="
                                            getFirstLocationAdditionalColors() >
                                                0 &&
                                            getAdditionalColorCost() > 0
                                        "
                                    >
                                        <td>
                                            Addl Color ({{
                                                getFirstLocationAdditionalColors()
                                            }})
                                        </td>
                                        <td>
                                            {{
                                                quantity *
                                                getFirstLocationAdditionalColors()
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getAdditionalColorCost()
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getAdditionalColorCost() *
                                                        getFirstLocationAdditionalColors() *
                                                        quantity
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Color Upcharge for 1st Location -->
                                    <tr
                                        v-if="
                                            getFirstLocationColorUpcharge() > 0
                                        "
                                    >
                                        <td>Specialty Ink Fee</td>
                                        <td>{{ quantity }}</td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getFirstLocationColorUpcharge()
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getFirstLocationColorUpcharge() *
                                                        quantity
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Name/Number Fees for 1st Location (Front) -->
                                    <tr
                                        v-for="(
                                            fee, feeIdx
                                        ) in getFirstLocationNameNumberFees()"
                                        :key="'fee-1st-' + feeIdx"
                                    >
                                        <td>{{ fee.name }}</td>
                                        <td>{{ fee.quantity || quantity }}</td>
                                        <td>
                                            {{ formatPrice(fee.perItemFee) }}
                                        </td>
                                        <td>{{ formatPrice(fee.total) }}</td>
                                    </tr>

                                    <!-- Additional Locations (2nd, 3rd, 4th, etc.) -->
                                    <template
                                        v-for="(
                                            row, rowIndex
                                        ) in additionalLocationRows"
                                    >
                                        <tr
                                            v-if="
                                                row.type === 'location' ||
                                                row.type === 'color1' ||
                                                row.type === 'addlColor' ||
                                                row.type === 'nameNumberFee' ||
                                                row.type === 'colorUpcharge'
                                            "
                                            :key="
                                                'loc-' +
                                                row.locationIndex +
                                                '-' +
                                                row.type +
                                                '-' +
                                                rowIndex
                                            "
                                        >
                                            <td>
                                                <span
                                                    v-if="
                                                        row.type === 'location'
                                                    "
                                                    ><strong
                                                        >Additional Location ({{
                                                            row.locationName
                                                        }})</strong
                                                    ></span
                                                >
                                                <span
                                                    v-else-if="
                                                        row.type === 'color1'
                                                    "
                                                >
                                                    <span v-if="row.isFullColor"
                                                        >Full Color</span
                                                    >
                                                    <span v-else
                                                        >1st Color</span
                                                    >
                                                </span>
                                                <span
                                                    v-else-if="
                                                        row.type === 'addlColor'
                                                    "
                                                    >Addl Color{{
                                                        row.colorCount
                                                            ? " (" +
                                                              row.colorCount +
                                                              ")"
                                                            : ""
                                                    }}</span
                                                >
                                                <span
                                                    v-else-if="
                                                        row.type ===
                                                        'nameNumberFee'
                                                    "
                                                    >{{ row.name }}</span
                                                >
                                                <span
                                                    v-else-if="
                                                        row.type ===
                                                        'colorUpcharge'
                                                    "
                                                    >Specialty Ink Fee</span
                                                >
                                            </td>
                                            <td>{{ row.qty }}</td>
                                            <td>{{ formatPrice(row.amt) }}</td>
                                            <td>
                                                {{ formatPrice(row.total) }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Stitches over 10,000 (if applicable) -->
                                    <tr
                                        v-if="
                                            stitchCount > 10000 &&
                                            getStitchCost() > 0
                                        "
                                    >
                                        <td>
                                            Stitches over 10,000 ({{
                                                getRemainingStitches()
                                            }}
                                            stitches)
                                        </td>
                                        <td>{{ quantity }}</td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getStitchCost() / quantity
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{ formatPrice(getStitchCost()) }}
                                        </td>
                                    </tr>

                                    <!-- Services grouped by Rollup Fee (rollup_fee_id = 12 for Run Charge) -->
                                    <template
                                        v-for="rollupGroup in getServicesGroupedByRollupFee(
                                            12
                                        )"
                                    >
                                        <!-- Services with show_service = false (grayed out, but show price) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === false;
                                                }
                                            )"
                                            :key="service.id"
                                            class="disable_data"
                                        >
                                            <td>
                                                {{ getServiceName(service) }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                        </tr>

                                        <!-- Services with show_service = true (normal display) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === true;
                                                }
                                            )"
                                            :key="service.id"
                                        >
                                            <td>
                                                {{ getServiceName(service) }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>-</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Other Run Charge Details (only non-location-specific fees) -->
                                    <tr
                                        v-for="(
                                            detail, detailIdx
                                        ) in getOtherRunChargeDetails()"
                                        :key="'detail-' + detailIdx"
                                    >
                                        <td>{{ detail.name }}</td>
                                        <td v-if="detail.perItem">
                                            {{ quantity }}
                                        </td>
                                        <td v-else>-</td>
                                        <td v-if="detail.perItem">
                                            {{ formatPrice(detail.perItemFee) }}
                                        </td>
                                        <td v-else>
                                            {{ formatPrice(detail.value) }}
                                        </td>
                                        <td>{{ formatPrice(detail.value) }}</td>
                                    </tr>

                                    <!-- Total Run Charge -->
                                    <tr class="total-row">
                                        <td colspan="3">
                                            <strong>Total</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(
                                                    calculatedRunChargeTotal
                                                )
                                            }}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <span class="cost-label" v-else> Run Charge </span>

                <span class="cost-value">{{
                    formatPrice(calculatedRunChargeTotal)
                }}</span>
            </div>
            <div class="cost-row" v-if="calculatedColorMixFeeTotal > 0">
                <div
                    class="tooltip-wrapper"
                    @click.stop="toggleColorMixFeeTooltip"
                    v-if="hasColorMixFeeData()"
                >
                    <span class="cost-label">Custom Color Fee</span>
                    <svg
                        class="info-icon-svg"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <div
                        class="tooltip-content"
                        :class="{ 'is-visible': colorMixFeeTooltipVisible }"
                    >
                        <div class="tooltip-header">
                            <h4>Custom Color Fee Breakdown</h4>
                        </div>
                        <div class="tooltip-body">
                            <table class="base-fee-table">
                                <thead>
                                    <tr>
                                        <th>Fee</th>
                                        <th>Qty</th>
                                        <th>Amt</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="getOrderMixFeeData()">
                                        <td>
                                            <strong>
                                                <span
                                                    v-if="isEmbroideryService()"
                                                    >Custom Color Thread Fee</span
                                                >
                                                <span
                                                    v-else-if="isPrintService()"
                                                    >Custom Color Ink Fee</span
                                                >
                                                <span v-else
                                                    >Custom Color Fee</span
                                                >
                                            </strong>
                                        </td>
                                        <td>
                                            {{
                                                getOrderMixFeeData()
                                                    .mixColorCount
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getOrderMixFeeData()
                                                        .unitPrice
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getOrderMixFeeData().total
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Services grouped by Rollup Fee (rollup_fee_id = 13 for Custom Color Fee) -->
                                    <template
                                        v-for="rollupGroup in getServicesGroupedByRollupFee(
                                            13
                                        )"
                                    >
                                        <!-- Services with show_service = false (grayed out, but show price) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === false;
                                                }
                                            )"
                                            :key="service.id"
                                            class="disable_data"
                                        >
                                            <td>
                                                {{ getServiceName(service) }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                        </tr>

                                        <!-- Services with show_service = true (normal display) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === true;
                                                }
                                            )"
                                            :key="service.id"
                                        >
                                            <td>
                                                {{ getServiceName(service) }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>-</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <tr class="total-row">
                                        <td colspan="3">
                                            <strong>Total</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(
                                                    calculatedColorMixFeeTotal
                                                )
                                            }}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <span class="cost-label" v-else> Custom Color Fee </span>
                <span class="cost-value">{{
                    formatPrice(calculatedColorMixFeeTotal)
                }}</span>
            </div>
            <div class="cost-row" v-if="calculatedSetupFeeTotal > 0">
                <div
                    class="tooltip-wrapper"
                    @click.stop="toggleSetupFeeTooltip"
                    v-if="hasSetupFeeData()"
                >
                    <span class="cost-label">Setup Fee</span>
                    <svg
                        class="info-icon-svg"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clip-rule="evenodd"
                        />
                    </svg>

                    <div
                        class="tooltip-content"
                        :class="{ 'is-visible': setupFeeTooltipVisible }"
                    >
                        <div class="tooltip-header">
                            <h4>Setup Fee Breakdown</h4>
                        </div>
                        <div class="tooltip-body">
                            <table class="base-fee-table">
                                <thead>
                                    <tr>
                                        <th>Fee</th>
                                        <th>Qty</th>
                                        <th>Amt</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <!-- Setup Fee Location 1 -->
                                    <tr
                                        v-if="
                                            getFirstLocationData() &&
                                            getFirstLocationColorCount() > 0
                                        "
                                    >
                                        <td>
                                            <strong
                                                >Set Up Fee Location 1{{
                                                    getFirstLocationName() !==
                                                    "1st Location"
                                                        ? " (" +
                                                          getFirstLocationName() +
                                                          ")"
                                                        : ""
                                                }}</strong
                                            >
                                        </td>
                                        <td
                                            v-if="
                                                getServiceFeeStructure() ===
                                                    '1' ||
                                                getServiceFeeStructure() === 1
                                            "
                                        >
                                            {{ getFirstLocationColorCount() }}
                                        </td>
                                        <td v-else>1</td>
                                        <td
                                            v-if="
                                                getServiceFeeStructure() ===
                                                    '1' ||
                                                getServiceFeeStructure() === 1
                                            "
                                        >
                                            {{
                                                formatPrice(
                                                    getServiceMasterData(
                                                        "setup_fee_color1"
                                                    )
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            <span
                                                v-if="
                                                    isFirstLocationFullColor() ||
                                                    getFirstLocationColorCount() >
                                                        1
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceMasterData(
                                                            "setup_fee_multicolor"
                                                        )
                                                    )
                                                }}
                                            </span>
                                            <span v-else>
                                                {{
                                                    formatPrice(
                                                        getServiceMasterData(
                                                            "setup_fee_color1"
                                                        )
                                                    )
                                                }}
                                            </span>
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getLocation1SetupFee()
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Setup Fee for Additional Locations -->
                                    <template
                                        v-for="(
                                            location, idx
                                        ) in getAdditionalLocations()"
                                    >
                                        <tr
                                            v-if="location.colorCount > 0"
                                            :key="'setup-loc-' + location.index"
                                        >
                                            <td>
                                                <strong
                                                    >Set Up Fee Location
                                                    {{ idx + 2
                                                    }}{{
                                                        location.locationName
                                                            ? " (" +
                                                              location.locationName +
                                                              ")"
                                                            : ""
                                                    }}</strong
                                                >
                                            </td>
                                            <td
                                                v-if="
                                                    getServiceFeeStructure() ===
                                                        '1' ||
                                                    getServiceFeeStructure() ===
                                                        1
                                                "
                                            >
                                                {{ location.colorCount }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    getServiceFeeStructure() ===
                                                        '1' ||
                                                    getServiceFeeStructure() ===
                                                        1
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceMasterData(
                                                            "setup_fee_color1"
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                <span
                                                    v-if="
                                                        location.isFullColor ===
                                                            true ||
                                                        location.colorCount > 1
                                                    "
                                                >
                                                    {{
                                                        formatPrice(
                                                            getServiceMasterData(
                                                                "setup_fee_multicolor"
                                                            )
                                                        )
                                                    }}
                                                </span>
                                                <span v-else>
                                                    {{
                                                        formatPrice(
                                                            getServiceMasterData(
                                                                "setup_fee_color1"
                                                            )
                                                        )
                                                    }}
                                                </span>
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getAdditionalLocationSetupFee(
                                                            location
                                                        )
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Services grouped by Rollup Fee (rollup_fee_id = 2 for Setup Fee) -->
                                    <template
                                        v-for="rollupGroup in getServicesGroupedByRollupFee(
                                            2
                                        )"
                                    >
                                        <!-- Services included in Setup Fee (show_service = false) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === false;
                                                }
                                            )"
                                            :key="service.id"
                                            class="disable_data"
                                        >
                                            <td>
                                                {{ getServiceName(service) }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>-</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                        </tr>

                                        <!-- Services shown as separate lines (show_service = true) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === true;
                                                }
                                            )"
                                            :key="service.id"
                                        >
                                            <td>
                                                {{ getServiceName(service) }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(service)
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Total Setup Fee -->
                                    <tr class="total-row">
                                        <td colspan="3">
                                            <strong>Total</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(
                                                    calculatedSetupFeeTotal
                                                )
                                            }}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <span class="cost-label" v-else> Setup Fee </span>
                <span class="cost-value">{{
                    formatPrice(calculatedSetupFeeTotal)
                }}</span>
            </div>
            <div class="cost-row" v-if="calculatedDiscount > 0">
                <span class="cost-label font-weight-bold">Subtotal</span>
                <span class="cost-value font-weight-bold">{{
                    formatPrice(preDiscountTotal)
                }}</span>
            </div>
            <div class="cost-row discount-row" v-if="calculatedDiscount > 0">
                <span class="cost-label">Discount</span>
                <span class="cost-value discount-value"
                    >-{{ formatPrice(calculatedDiscount) }}</span
                >
            </div>
            <hr class="cost-separator" />
            <div class="cost-row total-row">
                <h5 class="cost-label-total">Total Cost</h5>
                <h5 class="cost-value-total">
                    {{ formatPrice(calculatedTotalCost) }}
                </h5>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "PCNAPriceBreakdown",
    data() {
        return {
            tooltipVisible: false,
            runChargeTooltipVisible: false,
            setupFeeTooltipVisible: false,
            colorMixFeeTooltipVisible: false
        };
    },
    props: {
        quantity: { type: Number, default: 0 },
        otherServices: { type: Array, default: () => [] },
        baseCost: { type: Number, default: 0 },
        runCharge: { type: Number, default: 0 },
        runChargeDetails: { type: Array, default: () => [] },
        setupFee: { type: Number, default: 0 },
        subtotal: { type: Number, default: 0 },
        totalDiscount: { type: Number, default: 0 },
        discountConfig: { type: Object, default: () => null },
        imprintLocations: { type: Array, default: () => [] },
        serviceMasterData: { type: Object, default: () => ({}) },
        stitchCount: { type: Number, default: 0 },
        vendorColors: { type: Array, default: () => [] },
        colorSizeCombinations: { type: Array, default: () => [] }
    },
    computed: {
        displayBaseCost() {
            // Only include services with rollup_fee_id = 1 (Base Fee)
            const baseFeeServices = this.getServicesGroupedByRollupFee(1);
            let includedServicesTotal = 0; // Services with show_service = false
            let otherServicesTotal = 0; // Services with show_service = true

            baseFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    const show =
                        service.show_service !== undefined
                            ? service.show_service
                            : true;
                    if (show === false) {
                        // Included services: unit price × quantity
                        const serviceUnitPrice =
                            this.getServiceUnitPrice(service);
                        includedServicesTotal +=
                            serviceUnitPrice * this.quantity;
                    } else {
                        // Other services: use getServicePrice which handles Per Item vs Per Order
                        otherServicesTotal += this.getServicePrice(service);
                    }
                });
            });

            return parseFloat(
                (
                    this.baseCost +
                    includedServicesTotal +
                    otherServicesTotal
                ).toFixed(2)
            );
        },
        preDiscountTotal() {
            const base = this.displayBaseCost || 0;
            const runCharge = this.calculatedRunChargeTotal || 0;
            const colorMixFee = this.calculatedColorMixFeeTotal || 0;
            const setupFee = this.calculatedSetupFeeTotal || 0;
            return parseFloat(
                (base + runCharge + colorMixFee + setupFee).toFixed(2)
            );
        },
        // Calculate discount based on displayed preDiscountTotal (like calculators do)
        calculatedDiscount() {
            if (!this.discountConfig || !this.discountConfig.status) {
                return 0;
            }
            const preTotal = this.preDiscountTotal;
            if (preTotal <= 0) {
                return 0;
            }
            const discountType = this.discountConfig.discount_type;
            const discountValue =
                parseFloat(this.discountConfig.discount_value) || 0;

            if (discountType === "Percent") {
                return parseFloat(
                    ((preTotal * discountValue) / 100).toFixed(2)
                );
            } else {
                // Fixed amount
                return parseFloat(Math.min(preTotal, discountValue).toFixed(2));
            }
        },
        // Calculate total cost based on computed values
        calculatedTotalCost() {
            // Use calculatedDiscount instead of totalDiscount prop
            const discount = this.calculatedDiscount || 0;
            return parseFloat((this.preDiscountTotal - discount).toFixed(2));
        },
        // Calculate actual run charge total including location-wise mix fees
        calculatedRunChargeTotal() {
            let total = 0;

            // Add first location base cost
            if (this.getFirstLocationData()) {
                const firstLocationBaseCost = this.getFirstLocationBaseCost();
                if (firstLocationBaseCost > 0) {
                    total += firstLocationBaseCost * this.quantity;
                }

                // Check if full color is selected for first location
                if (this.isFirstLocationFullColor()) {
                    // For full color, use full color value instead of regular color calculation
                    const fullColorValue =
                        this.getFirstLocationFullColorValue();
                    if (fullColorValue > 0) {
                        total += fullColorValue * this.quantity;
                    }
                } else {
                    // Regular color calculation
                    // Add first color cost
                    const firstColorCost = this.getFirstColorCost();
                    if (firstColorCost > 0) {
                        total += firstColorCost * this.quantity;
                    }

                    // Add additional colors cost
                    const additionalColors =
                        this.getFirstLocationAdditionalColors();
                    if (additionalColors > 0) {
                        const additionalColorCost =
                            this.getAdditionalColorCost();
                        if (additionalColorCost > 0) {
                            total +=
                                additionalColorCost *
                                additionalColors *
                                this.quantity;
                        }
                    }
                }

                // Add first location color upcharge
                const firstLocationColorUpcharge =
                    this.getFirstLocationColorUpcharge();
                if (firstLocationColorUpcharge > 0) {
                    total += firstLocationColorUpcharge * this.quantity;
                }

                // Add first location name/number fees
                const firstLocationNameNumberFees =
                    this.getFirstLocationNameNumberFees();
                firstLocationNameNumberFees.forEach((fee) => {
                    total += fee.total;
                });
            }

            // Add additional locations
            const additionalLocations = this.getAdditionalLocations();
            additionalLocations.forEach((location) => {
                // Base cost
                const additionalLocationBaseCost =
                    this.getAdditionalLocationBaseCost();
                if (additionalLocationBaseCost > 0) {
                    total += additionalLocationBaseCost * this.quantity;
                }

                // Check if full color is selected for this location
                if (location.isFullColor === true) {
                    // For full color, use same value as first location
                    const fullColorValue =
                        this.getFirstLocationFullColorValue();
                    if (fullColorValue > 0) {
                        total += fullColorValue * this.quantity;
                    }
                } else if (location.colorCount > 0) {
                    // Regular color calculation
                    // 1st Color
                    const firstColorCost = this.getFirstColorCost();
                    if (firstColorCost > 0) {
                        total += firstColorCost * this.quantity;
                    }

                    // Additional colors
                    if (location.colorCount > 1) {
                        const additionalColorCost =
                            this.getAdditionalColorCost();
                        if (additionalColorCost > 0) {
                            total +=
                                additionalColorCost *
                                (location.colorCount - 1) *
                                this.quantity;
                        }
                    }
                }

                // Color upcharge for this location
                const locationColorUpcharge = this.getLocationColorUpcharge(
                    location.location
                );
                if (locationColorUpcharge > 0) {
                    total += locationColorUpcharge * this.quantity;
                }

                // Name/number fees for this location
                const locationNameNumberFees = this.getLocationNameNumberFees(
                    location.location
                );
                locationNameNumberFees.forEach((fee) => {
                    total += fee.total;
                });
            });

            // Add stitch cost if applicable
            if (this.stitchCount > 10000) {
                const stitchCost = this.getStitchCost();
                if (stitchCost > 0) {
                    total += stitchCost;
                }
            }

            // Add other run charge details (excluding location-specific fees)
            const otherRunChargeDetails = this.getOtherRunChargeDetails();
            otherRunChargeDetails.forEach((detail) => {
                total += parseFloat(detail.value || 0);
            });

            // Add services with rollup_fee_id = 12 (Run Charge services) - include all services regardless of show_service
            const runChargeServices = this.getServicesGroupedByRollupFee(12);
            runChargeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    total += this.getServicePrice(service);
                });
            });

            return parseFloat(total.toFixed(2));
        },
        // Calculate total setup fee from all locations
        calculatedSetupFeeTotal() {
            let total = 0;

            // Add Location 1 setup fee (only if it has colors or full color)
            const firstLocation =
                this.imprintLocations &&
                this.imprintLocations.find((loc) => loc.is_selected);
            if (firstLocation && firstLocation.is_selected) {
                // Check if full color is selected or has color count
                if (
                    this.isFirstLocationFullColor() ||
                    this.getFirstLocationColorCount() > 0
                ) {
                    const location1Fee = this.getLocation1SetupFee();
                    total += location1Fee;
                }
            }

            // Add all additional location setup fees (each location calculated independently)
            const additionalLocations = this.getAdditionalLocations();
            additionalLocations.forEach((location) => {
                // Check if full color or has color count
                if (location.isFullColor === true || location.colorCount > 0) {
                    const locationFee =
                        this.getAdditionalLocationSetupFee(location);
                    total += locationFee;
                }
            });

            // Add services with fee_id = 2 (Setup Fee services) - include all services regardless of show_service
            const setupFeeServices = this.getServicesGroupedByRollupFee(2);
            setupFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    total += this.getServicePrice(service);
                });
            });

            return parseFloat(total.toFixed(2));
        },
        // Calculate Custom Color Fee total
        calculatedColorMixFeeTotal() {
            let total = 0;
            
            const mixFeeData = this.getOrderMixFeeData();
            if (mixFeeData && mixFeeData.total > 0) {
                total += mixFeeData.total;
            }

            // Add services with rollup_fee_id = 13 (Custom Color Fee services) - include all services regardless of show_service
            const colorMixFeeServices = this.getServicesGroupedByRollupFee(13);
            colorMixFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    total += this.getServicePrice(service);
                });
            });

            return parseFloat(total.toFixed(2));
        },
        additionalLocationRows() {
            const locations = this.getAdditionalLocations();
            const rows = [];
            locations.forEach((location, idx) => {
                // Calculate total for this additional location to check if it should be shown
                let additionalLocationTotal = 0;

                // Base location cost
                const baseCost =
                    this.getAdditionalLocationBaseCost() * this.quantity;
                additionalLocationTotal += baseCost;

                // 1st Color cost
                if (location.colorCount > 0) {
                    const firstColorTotal =
                        this.getFirstColorCost() * this.quantity;
                    if (firstColorTotal > 0) {
                        additionalLocationTotal += firstColorTotal;
                    }
                }

                // Additional colors cost
                if (location.colorCount > 1) {
                    const addlColorTotal =
                        this.getAdditionalColorCost() *
                        (location.colorCount - 1) *
                        this.quantity;
                    if (addlColorTotal > 0) {
                        additionalLocationTotal += addlColorTotal;
                    }
                }

                // Color Upcharge for this location
                const locationColorUpcharge = this.getLocationColorUpcharge(
                    location.location
                );
                if (locationColorUpcharge > 0) {
                    additionalLocationTotal +=
                        locationColorUpcharge * this.quantity;
                }

                // Name/Number Fees for this additional location
                const nameNumberFees = this.getLocationNameNumberFees(
                    location.location
                );
                nameNumberFees.forEach((fee) => {
                    if (fee.total > 0) {
                        additionalLocationTotal += fee.total;
                    }
                });

                // Only add rows if total > 0
                if (additionalLocationTotal > 0) {
                    // Base location row - locationIndex starts from 1 for display (Additional Location 1, 2, 3...)
                    rows.push({
                        type: "location",
                        locationIndex: idx + 1,
                        locationName: location.locationName,
                        qty: this.quantity,
                        amt: this.getAdditionalLocationBaseCost(),
                        total:
                            this.getAdditionalLocationBaseCost() * this.quantity
                    });

                    // 1st Color row or Full Color row
                    if (location.isFullColor && location.fullColorValue) {
                        // Full color row
                        rows.push({
                            type: "color1",
                            locationIndex: idx + 1,
                            locationName: location.locationName,
                            qty: this.quantity,
                            amt: parseFloat(location.fullColorValue) || 0,
                            total:
                                (parseFloat(location.fullColorValue) || 0) *
                                this.quantity,
                            isFullColor: true
                        });
                    } else if (
                        location.colorCount > 0 &&
                        this.getFirstColorCost() * this.quantity > 0
                    ) {
                        rows.push({
                            type: "color1",
                            locationIndex: idx + 1,
                            locationName: location.locationName,
                            qty: this.quantity,
                            amt: this.getFirstColorCost(),
                            total: this.getFirstColorCost() * this.quantity,
                            isFullColor: false
                        });
                    }

                    // Additional colors row
                    if (
                        location.colorCount > 1 &&
                        this.getAdditionalColorCost() *
                            (location.colorCount - 1) *
                            this.quantity >
                            0
                    ) {
                        rows.push({
                            type: "addlColor",
                            locationIndex: idx + 1,
                            locationName: location.locationName,
                            qty: this.quantity * (location.colorCount - 1),
                            amt: this.getAdditionalColorCost(),
                            total:
                                this.getAdditionalColorCost() *
                                (location.colorCount - 1) *
                                this.quantity,
                            colorCount: location.colorCount - 1 // Store additional color count for display
                        });
                    }

                    // Color Upcharge for this location
                    if (locationColorUpcharge > 0) {
                        rows.push({
                            type: "colorUpcharge",
                            locationIndex: idx + 1,
                            locationName: location.locationName,
                            qty: this.quantity,
                            amt: locationColorUpcharge,
                            total: locationColorUpcharge * this.quantity
                        });
                    }

                    // Name/Number Fees for this additional location
                    nameNumberFees.forEach((fee) => {
                        if (fee.total > 0) {
                            rows.push({
                                type: "nameNumberFee",
                                locationIndex: idx + 1,
                                locationName: location.locationName,
                                name: fee.name,
                                qty: fee.quantity || this.quantity,
                                amt: fee.perItemFee,
                                total: fee.total
                            });
                        }
                    });
                }
            });
            return rows;
        }
    },
    methods: {
        formatPrice(value) {
            if (value === null || isNaN(value)) return "$0.00";
            return "$" + parseFloat(value).toFixed(2);
        },
        toggleTooltip() {
            this.tooltipVisible = !this.tooltipVisible;
        },
        toggleRunChargeTooltip() {
            this.runChargeTooltipVisible = !this.runChargeTooltipVisible;
            this.tooltipVisible = false;
            this.setupFeeTooltipVisible = false;
        },
        toggleSetupFeeTooltip() {
            this.setupFeeTooltipVisible = !this.setupFeeTooltipVisible;
            this.tooltipVisible = false;
            this.runChargeTooltipVisible = false;
            this.colorMixFeeTooltipVisible = false;
        },
        toggleColorMixFeeTooltip() {
            this.colorMixFeeTooltipVisible = !this.colorMixFeeTooltipVisible;
            this.tooltipVisible = false;
            this.runChargeTooltipVisible = false;
            this.setupFeeTooltipVisible = false;
        },
        // Check if we have data to show run charge breakdown
        hasRunChargeData() {
            return (
                (this.imprintLocations && this.imprintLocations.length > 0) ||
                (this.serviceMasterData &&
                    Object.keys(this.serviceMasterData).length > 0) ||
                (this.runChargeDetails && this.runChargeDetails.length > 0)
            );
        },
        // Check if we have data to show custom color fee breakdown
        hasColorMixFeeData() {
            // Check if there's Custom Color Fee data from runChargeDetails
            const mixFeeData = this.getOrderMixFeeData();
            if (mixFeeData && mixFeeData.total > 0) {
                return true;
            }
            
            // Check if there are roll up id 13 services
            const colorMixFeeServices = this.getServicesGroupedByRollupFee(13);
            if (colorMixFeeServices && colorMixFeeServices.length > 0) {
                return colorMixFeeServices.some(
                    (group) => group.services && group.services.length > 0
                );
            }
            
            return false;
        },
        // Check if we have data to show setup fee breakdown
        hasSetupFeeData() {
            // Check if there are roll up id 2 services (Setup Fee services)
            const setupFeeServices = this.getServicesGroupedByRollupFee(2);
            if (setupFeeServices && setupFeeServices.length > 0) {
                const hasServices = setupFeeServices.some(
                    (group) => group.services && group.services.length > 0
                );
                if (hasServices) {
                    return true;
                }
            }

            // Show tooltip if there's at least one location with colors (even single location)
            if (
                !this.serviceMasterData ||
                Object.keys(this.serviceMasterData).length === 0
            ) {
                return false;
            }

            // Check if first location has colors (show even for single location)
            if (this.imprintLocations && this.imprintLocations.length > 0) {
                const firstLocation = this.imprintLocations.find(
                    (loc) => loc.is_selected
                );
                if (firstLocation && firstLocation.is_selected) {
                    // Check if full color is selected
                    if (
                        firstLocation.is_full_color_selected === true ||
                        firstLocation.no_of_color === "full_color"
                    ) {
                        return true;
                    }
                    // Check if regular color count > 0
                    if (
                        firstLocation.no_of_color &&
                        parseInt(firstLocation.no_of_color) > 0
                    ) {
                        return true;
                    }
                }
            }

            // Check if any additional locations have colors
            const additionalLocations = this.getAdditionalLocations();
            return additionalLocations.length > 0;
        },
        closeTooltipOnClickOutside(event) {
            const wrappers = this.$el.querySelectorAll(".tooltip-wrapper");
            let clickedInsideAnyWrapper = false;

            wrappers.forEach((wrapper) => {
                if (wrapper.contains(event.target)) {
                    clickedInsideAnyWrapper = true;
                }
            });
            if (!clickedInsideAnyWrapper) {
                this.tooltipVisible = false;
                this.runChargeTooltipVisible = false;
                this.setupFeeTooltipVisible = false;
                this.colorMixFeeTooltipVisible = false;
            }
        },
        // Get base unit price (including included services)
        getBaseUnitPrice() {
            let baseUnitPrice = 0;
            if (this.quantity > 0 && this.baseCost > 0) {
                baseUnitPrice = parseFloat(this.baseCost / this.quantity);
            }

            // Add included services unit price
            const includedServicesUnitPrice =
                this.getIncludedServicesUnitPrice();
            return parseFloat(
                (baseUnitPrice + includedServicesUnitPrice).toFixed(2)
            );
        },
        // Get total of services with show_service = false (INCL services)
        getIncludedServicesTotal() {
            const baseFeeServices = this.getServicesGroupedByRollupFee(1);
            let includedServicesTotal = 0;

            baseFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    const show =
                        service.show_service !== undefined
                            ? service.show_service
                            : true;
                    if (show === false) {
                        includedServicesTotal += this.getServicePrice(service);
                    }
                });
            });

            return includedServicesTotal;
        },
        // Get Product Base Price total including INCL services
        getProductBasePriceTotal() {
            const includedServicesUnitPrice =
                this.getIncludedServicesUnitPrice();
            const includedServicesTotal =
                includedServicesUnitPrice * this.quantity;
            return parseFloat(
                (this.baseCost + includedServicesTotal).toFixed(2)
            );
        },
        // Get services by show_service status
        getServicesByShowService(showService) {
            if (!this.otherServices) return [];
            return this.otherServices.filter((service) => {
                const show =
                    service.show_service !== undefined
                        ? service.show_service
                        : true;
                return show === showService;
            });
        },
        // Get services grouped by rollup fee ID and name for a specific fee ID
        getServicesGroupedByRollupFee(feeId) {
            if (!this.otherServices || this.otherServices.length === 0)
                return [];

            // Filter services by the specified fee_id
            const filteredServices = this.otherServices.filter((service) => {
                const rollupService = service.rollup_service || {};
                const serviceFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;
                return serviceFeeId === feeId;
            });

            if (filteredServices.length === 0) return [];

            // Group by rollup_fee_id (should all be the same fee_id, but grouping for consistency)
            const grouped = {};

            filteredServices.forEach((service) => {
                const rollupService = service.rollup_service || {};
                const rollupFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;

                if (rollupFeeId !== undefined && rollupFeeId !== null) {
                    if (!grouped[rollupFeeId]) {
                        // Get rollup fee name from rollup_service or service
                        const rollupFeeName =
                            rollupService.rollup_fee ||
                            service.rollup_fee_name ||
                            `Rollup Fee ${rollupFeeId}`;

                        grouped[rollupFeeId] = {
                            rollup_fee_id: rollupFeeId,
                            rollup_fee_name: rollupFeeName,
                            services: []
                        };
                    }
                    grouped[rollupFeeId].services.push(service);
                }
            });

            // Convert to array and sort by rollup_fee_id
            return Object.values(grouped).sort(
                (a, b) => a.rollup_fee_id - b.rollup_fee_id
            );
        },
        // Get service price
        getServicePrice(service) {
            const cost = parseFloat(service.retail_cost) || 0;
            const rollupService = service.rollup_service || {};
            const feeStructure =
                service.fee_structure === "Per Item" ||
                rollupService.fee_structure_name === "Per Item";
            if (feeStructure) {
                return cost * this.quantity;
            }
            return cost;
        },
        // Get service unit price
        getServiceUnitPrice(service) {
            return parseFloat(service.retail_cost) || 0;
        },
        // Get service name safely
        getServiceName(service) {
            if (service.name) {
                return service.name;
            }
            if (service.master_service && service.master_service.service_name) {
                return service.master_service.service_name;
            }
            return "Service";
        },
        // Get service master data field
        getServiceMasterData(field) {
            if (
                this.serviceMasterData &&
                this.serviceMasterData[field] !== undefined
            ) {
                return parseFloat(this.serviceMasterData[field]) || 0;
            }
            return 0;
        },
        // Get service fee structure
        getServiceFeeStructure() {
            if (
                this.serviceMasterData &&
                this.serviceMasterData.fee_structure !== undefined
            ) {
                // Return as both number and string for flexible comparison
                const feeStruct = this.serviceMasterData.fee_structure;
                return typeof feeStruct === "string"
                    ? feeStruct
                    : String(feeStruct);
            }
            return "1"; // Default to fee structure 1
        },
        // Get first location color count
        getFirstLocationColorCount() {
            if (!this.imprintLocations || this.imprintLocations.length === 0)
                return 0;
            const firstLocation =
                this.imprintLocations.find((loc) => loc.is_selected) ||
                this.imprintLocations[0];
            if (firstLocation && firstLocation.is_selected) {
                // Check if full color is selected
                if (
                    firstLocation.is_full_color_selected === true ||
                    firstLocation.no_of_color === "full_color"
                ) {
                    return 1; // Return 1 for full color
                }
                return parseInt(firstLocation.no_of_color) || 0;
            }
            return 0;
        },
        // Get location 1 setup fee
        getLocation1SetupFee() {
            // Check if full color is selected for first location
            if (this.isFirstLocationFullColor()) {
                return this.getServiceMasterData("setup_fee_multicolor") || 0;
            }

            const colorCount = this.getFirstLocationColorCount();
            if (colorCount === 0) return 0;

            const feeStructure = this.getServiceFeeStructure();
            // Handle both string and number comparison
            const feeStructNum = parseInt(feeStructure);
            if (feeStructNum === 1 || feeStructure === "1") {
                // Per Color: multiply by color count
                return (
                    this.getServiceMasterData("setup_fee_color1") * colorCount
                );
            } else if (feeStructNum === 2 || feeStructure === "2") {
                // Tiered: each location calculated independently based on its own color count
                // For fee_structure 2, each location's setup fee is calculated separately
                if (colorCount === 1) {
                    return this.getServiceMasterData("setup_fee_color1");
                } else {
                    return this.getServiceMasterData("setup_fee_multicolor");
                }
            }
            return 0;
        },
        // Get additional location setup fee
        getAdditionalLocationSetupFee(location) {
            // Check if full color is selected for this location
            if (location.isFullColor === true) {
                return this.getServiceMasterData("setup_fee_multicolor") || 0;
            }

            const colorCount = location.colorCount;
            if (colorCount === 0) return 0;

            const feeStructure = this.getServiceFeeStructure();
            // Handle both string and number comparison
            const feeStructNum = parseInt(feeStructure);
            if (feeStructNum === 1 || feeStructure === "1") {
                // Per Color: multiply by color count
                return (
                    this.getServiceMasterData("setup_fee_color1") * colorCount
                );
            } else if (feeStructNum === 2 || feeStructure === "2") {
                // Tiered: each location calculated independently based on its own color count
                // For fee_structure 2, each location's setup fee is calculated separately
                if (colorCount === 1) {
                    return this.getServiceMasterData("setup_fee_color1");
                } else {
                    return this.getServiceMasterData("setup_fee_multicolor");
                }
            }
            return 0;
        },
        // Get services by show_service status and fee ID
        getServicesByShowServiceAndFeeId(showService, feeId) {
            if (!this.otherServices) return [];
            return this.otherServices.filter((service) => {
                const show =
                    service.show_service !== undefined
                        ? service.show_service
                        : true;
                const serviceFeeId =
                    service.rollup_service &&
                    service.rollup_service.rollup_fee_id
                        ? service.rollup_service.rollup_fee_id
                        : null;
                return show === showService && serviceFeeId === feeId;
            });
        },
        // Get first location data
        getFirstLocationData() {
            if (!this.imprintLocations || this.imprintLocations.length === 0) {
                // Fallback to runChargeDetails if available
                return (
                    this.runChargeDetails && this.runChargeDetails.length > 0
                );
            }
            const firstLocation = this.imprintLocations.find(
                (loc) => loc.is_selected
            );
            if (!firstLocation) return false;
            // Check if full color is selected
            if (
                firstLocation.is_full_color_selected === true ||
                firstLocation.no_of_color === "full_color"
            ) {
                return true;
            }
            return firstLocation && parseInt(firstLocation.no_of_color) > 0;
        },
        // Get first location base cost
        getFirstLocationBaseCost() {
            return this.getServiceMasterData("location_1st");
        },
        // Get first color cost
        getFirstColorCost() {
            // Check if first location has full color selected
            if (this.isFirstLocationFullColor()) {
                return this.getFirstLocationFullColorValue();
            }
            return this.getServiceMasterData("cost_per_color_1st");
        },
        // Check if first location has full color selected
        isFirstLocationFullColor() {
            if (!this.imprintLocations || this.imprintLocations.length === 0) {
                return false;
            }
            const firstLocation = this.imprintLocations.find(
                (loc) => loc.is_selected
            );
            if (!firstLocation) {
                return false;
            }
            return (
                firstLocation.is_full_color_selected === true ||
                firstLocation.no_of_color === "full_color"
            );
        },
        // Get full color value for first location
        getFirstLocationFullColorValue() {
            if (!this.imprintLocations || this.imprintLocations.length === 0) {
                return 0;
            }
            const firstLocation = this.imprintLocations.find(
                (loc) => loc.is_selected
            );
            if (firstLocation && firstLocation.full_color_value) {
                return parseFloat(firstLocation.full_color_value) || 0;
            }
            // Fallback to service master data
            return this.getServiceMasterData("full_color") || 0;
        },
        // Get additional color cost
        getAdditionalColorCost() {
            return this.getServiceMasterData("cost_per_color_2ndormore");
        },
        // Get additional location base cost
        getAdditionalLocationBaseCost() {
            return this.getServiceMasterData("additional_location");
        },
        // Get first location additional colors count
        getFirstLocationAdditionalColors() {
            if (!this.imprintLocations || this.imprintLocations.length === 0)
                return 0;
            const firstLocation =
                this.imprintLocations.find((loc) => loc.is_selected) ||
                this.imprintLocations[0];
            if (firstLocation && firstLocation.is_selected) {
                // If full color is selected, no additional colors
                if (
                    firstLocation.is_full_color_selected === true ||
                    firstLocation.no_of_color === "full_color"
                ) {
                    return 0;
                }
                const colorCount = parseInt(firstLocation.no_of_color) || 0;
                return colorCount > 1 ? colorCount - 1 : 0;
            }
            return 0;
        },
        // Get additional locations (2nd, 3rd, 4th, etc.)
        getAdditionalLocations() {
            if (!this.imprintLocations || this.imprintLocations.length <= 1)
                return [];

            const additionalLocations = [];
            let selectedCount = 0;
            // Find first selected location to skip it
            const firstSelectedIndex = this.imprintLocations.findIndex(
                (loc) => loc.is_selected
            );

            // Process all locations, but skip the first selected one
            for (let i = 0; i < this.imprintLocations.length; i++) {
                const location = this.imprintLocations[i];
                if (
                    location &&
                    location.is_selected &&
                    i !== firstSelectedIndex
                ) {
                    // Check if full color is selected
                    const isFullColor =
                        location.is_full_color_selected === true ||
                        location.no_of_color === "full_color";
                    const colorCount = isFullColor
                        ? 1
                        : parseInt(location.no_of_color) || 0;
                    if (colorCount > 0 || isFullColor) {
                        additionalLocations.push({
                            index: i,
                            locationName:
                                location.location || `Location ${i + 1}`,
                            colorCount: colorCount,
                            isFullColor: isFullColor,
                            fullColorValue: location.full_color_value,
                            location: location // Include full location object for color access
                        });
                    }
                }
            }
            return additionalLocations;
        },
        // Get first location name
        getFirstLocationName() {
            if (!this.imprintLocations || this.imprintLocations.length === 0) {
                return "1st Location";
            }
            const firstLocation = this.imprintLocations.find(
                (loc) => loc.is_selected
            );
            if (firstLocation && firstLocation.location) {
                return firstLocation.location;
            }
            // Fallback to first location in array
            if (this.imprintLocations[0] && this.imprintLocations[0].location) {
                return this.imprintLocations[0].location;
            }
            return "1st Location";
        },
        // Get color ink mix for first location
        getFirstLocationColorInkMix() {
            if (!this.imprintLocations || this.imprintLocations.length === 0) {
                return [];
            }
            // Find first selected location
            const firstLocation = this.imprintLocations.find(
                (loc) => loc.is_selected
            );
            if (!firstLocation) {
                // Fallback to first location in array
                return this.getLocationColorInkMix(this.imprintLocations[0]);
            }
            return this.getLocationColorInkMix(firstLocation);
        },
        // Get color ink mix for a specific location
        getLocationColorInkMix(location) {
            if (!location) {
                return [];
            }

            // Check if colors array exists
            const colorsArray = location.colors || [];
            if (!Array.isArray(colorsArray) || colorsArray.length === 0) {
                return [];
            }

            const inkMixColors = [];
            colorsArray.forEach((colorObj) => {
                // Check if color object exists and has the right structure
                if (!colorObj) return;

                const color = colorObj.color;
                if (!color) return;

                // Check if it's a custom color (color_id == 0) with non_standard_fee
                const colorId = parseInt(color.color_id);
                const nonStandardFee = parseFloat(color.non_standard_fee || 0);

                if (colorId === 0 && nonStandardFee > 0) {
                    inkMixColors.push({
                        colorName:
                            color.color_name ||
                            color.colorName ||
                            "Custom Color",
                        fee: nonStandardFee
                    });
                }
            });
            return inkMixColors;
        },
        // Get total custom color fee for first location (sum of all mix fees)
        getFirstLocationTotalColorMixFee() {
            const colorMix = this.getFirstLocationColorInkMix();
            if (!colorMix || colorMix.length === 0) {
                return 0;
            }
            // Sum all mix fees
            const totalFee = colorMix.reduce((sum, colorData) => {
                return sum + parseFloat(colorData.fee || 0);
            }, 0);
            return parseFloat(totalFee.toFixed(2));
        },
        // Get total custom color fee for a specific location (sum of all mix fees)
        getLocationTotalColorMixFee(location) {
            if (!location) {
                return 0;
            }
            const colorMix = this.getLocationColorInkMix(location);
            if (!colorMix || colorMix.length === 0) {
                return 0;
            }
            // Sum all mix fees
            const totalFee = colorMix.reduce((sum, colorData) => {
                return sum + parseFloat(colorData.fee || 0);
            }, 0);
            return parseFloat(totalFee.toFixed(2));
        },
        // Check if service is embroidery
        isEmbroideryService() {
            // First check serviceMasterData
            if (this.serviceMasterData) {
                const serviceName = (
                    this.serviceMasterData.service_name ||
                    this.serviceMasterData.ecomm_display ||
                    this.serviceMasterData.name ||
                    ""
                ).toLowerCase();
                if (serviceName) {
                    return (
                        serviceName.includes("embroidery") ||
                        serviceName.includes("emb")
                    );
                }
            }
            
            // Fallback: check from otherServices
            if (this.otherServices && this.otherServices.length > 0) {
                for (const service of this.otherServices) {
                    const serviceName = (
                        service.name ||
                        service.ecomm_display ||
                        (service.master_service && service.master_service.service_name) ||
                        ""
                    ).toLowerCase();
                    if (serviceName && (serviceName.includes("embroidery") || serviceName.includes("emb"))) {
                        return true;
                    }
                }
            }
            
            // Fallback: check from runChargeDetails (check if it's thread mix fee)
            if (this.runChargeDetails && this.runChargeDetails.length > 0) {
                const mixFeeDetail = this.runChargeDetails.find((detail) => {
                    if (!detail || !detail.name) return false;
                    const nameLower = detail.name.toLowerCase();
                    return nameLower.includes("thread mix fee") || nameLower.includes("custom color thread fee");
                });
                if (mixFeeDetail) return true;
            }
            
            return false;
        },
        // Check if service is print
        isPrintService() {
            // First check serviceMasterData
            if (this.serviceMasterData) {
                const serviceName = (
                    this.serviceMasterData.service_name ||
                    this.serviceMasterData.ecomm_display ||
                    this.serviceMasterData.name ||
                    ""
                ).toLowerCase();
                if (serviceName) {
                    return serviceName.includes("print");
                }
            }
            
            // Fallback: check from otherServices
            if (this.otherServices && this.otherServices.length > 0) {
                for (const service of this.otherServices) {
                    const serviceName = (
                        service.name ||
                        service.ecomm_display ||
                        (service.master_service && service.master_service.service_name) ||
                        ""
                    ).toLowerCase();
                    if (serviceName && serviceName.includes("print")) {
                        return true;
                    }
                }
            }
            
            // Fallback: check from runChargeDetails (check if it's ink mix fee)
            if (this.runChargeDetails && this.runChargeDetails.length > 0) {
                const mixFeeDetail = this.runChargeDetails.find((detail) => {
                    if (!detail || !detail.name) return false;
                    const nameLower = detail.name.toLowerCase();
                    return nameLower.includes("ink mix fee") || nameLower.includes("custom color ink fee");
                });
                if (mixFeeDetail) return true;
            }
            
            return false;
        },
        // Get Name Fee from VendorFees (using ecomm_display for matching)
        getNameFeeFromVendorServices() {
            if (!this.otherServices || !Array.isArray(this.otherServices)) {
                return null;
            }
            // First try to find by type (VendorFees structure)
            let nameService = this.otherServices.find(
                (service) => service.type === "name"
            );
            // If not found, try ecomm_display matching
            if (!nameService) {
                nameService = this.otherServices.find(
                    (service) =>
                        (service.ecomm_display &&
                            service.ecomm_display
                                .toLowerCase()
                                .includes("name")) ||
                        (service.name &&
                            service.name.toLowerCase().includes("name fees"))
                );
            }
            if (nameService && nameService.retail_cost) {
                return parseFloat(nameService.retail_cost) || 0;
            }
            return null;
        },
        // Get Number Fee from VendorFees (using ecomm_display for matching)
        getNumberFeeFromVendorServices() {
            if (!this.otherServices || !Array.isArray(this.otherServices)) {
                return null;
            }
            // First try to find by type (VendorFees structure)
            let numberService = this.otherServices.find(
                (service) => service.type === "number"
            );
            // If not found, try ecomm_display matching
            if (!numberService) {
                numberService = this.otherServices.find(
                    (service) =>
                        (service.ecomm_display &&
                            service.ecomm_display
                                .toLowerCase()
                                .includes("number")) ||
                        (service.name &&
                            service.name.toLowerCase().includes("number fees"))
                );
            }
            if (numberService && numberService.retail_cost) {
                return parseFloat(numberService.retail_cost) || 0;
            }
            return null;
        },
        // Get unique non-standard color count from imprint locations
        getUniqueNonStandardColorCount() {
            if (
                !this.imprintLocations ||
                !Array.isArray(this.imprintLocations)
            ) {
                return 0;
            }
            let uniqueColors = new Set();
            this.imprintLocations.forEach((location) => {
                if (
                    location.is_selected &&
                    location.colors &&
                    Array.isArray(location.colors)
                ) {
                    location.colors.forEach((colorObj) => {
                        if (colorObj && colorObj.color) {
                            const color = colorObj.color;
                            // Check if it's a custom color (color_id == 0)
                            if (parseInt(color.color_id) === 0) {
                                const hex =
                                    color.color_value || color.hex || "";
                                if (hex) {
                                    uniqueColors.add(hex.toLowerCase());
                                }
                            }
                        }
                    });
                }
            });
            return uniqueColors.size;
        },
        // Get name/number fees for first location (Front)
        getFirstLocationNameNumberFees() {
            const fees = [];
            const firstLocationName = this.getFirstLocationName().toLowerCase();

            // Try to get from runChargeDetails first (for backward compatibility)
            if (this.runChargeDetails && Array.isArray(this.runChargeDetails)) {
                // Front Number Fee
                const frontNumberFee = this.runChargeDetails.find(
                    (d) =>
                        d.name &&
                        d.name.toLowerCase().includes("front number fee") &&
                        !d.name.toLowerCase().includes("decor up")
                );
                if (
                    frontNumberFee &&
                    parseFloat(frontNumberFee.value || 0) > 0
                ) {
                    // Use unitPrice and quantity from runChargeDetails (frontNumberCount is already in quantity)
                    const perItemFee = parseFloat(
                        frontNumberFee.unitPrice || 0
                    );
                    const quantity = parseFloat(frontNumberFee.quantity || 0); // This is frontNumberCount from ItemDetails
                    const total = parseFloat(frontNumberFee.value || 0);
                    fees.push({
                        name: frontNumberFee.name,
                        perItemFee: perItemFee,
                        quantity: quantity,
                        total: total
                    });
                }
            }

            // Note: Fallback calculation removed - fees should always come from runChargeDetails
            // which is calculated based on actual names/numbers count, not color count
            // If runChargeDetails doesn't have the fee, it means there are no names/numbers added

            // Front Number Decor Up Fee (still from runChargeDetails)
            if (this.runChargeDetails && Array.isArray(this.runChargeDetails)) {
                const frontNumberDecorUp = this.runChargeDetails.find(
                    (d) =>
                        d.name &&
                        d.name
                            .toLowerCase()
                            .includes("front number decor up fee")
                );
                if (
                    frontNumberDecorUp &&
                    parseFloat(frontNumberDecorUp.value || 0) > 0
                ) {
                    // Get the quantity from Front Number Fee (not product quantity)
                    const frontNumberFee = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name.toLowerCase().includes("front number fee") &&
                            !d.name.toLowerCase().includes("decor up")
                    );
                    const quantity = frontNumberFee
                        ? parseFloat(frontNumberFee.quantity || 0)
                        : this.quantity;

                    // Calculate per-item fee: if unitPrice exists use it, otherwise divide total value by quantity
                    let perItemFee = 0;
                    if (frontNumberDecorUp.unitPrice) {
                        perItemFee = parseFloat(
                            frontNumberDecorUp.unitPrice || 0
                        );
                    } else if (quantity > 0) {
                        // Value is total, divide by quantity to get per-item price
                        perItemFee =
                            parseFloat(frontNumberDecorUp.value || 0) /
                            quantity;
                    } else {
                        perItemFee = parseFloat(frontNumberDecorUp.value || 0);
                    }
                    const total = perItemFee * quantity;
                    fees.push({
                        name: frontNumberDecorUp.name,
                        perItemFee: perItemFee,
                        quantity: quantity,
                        total: total
                    });
                }
            }

            return fees;
        },
        // Get name/number fees for a specific location
        getLocationNameNumberFees(location) {
            if (!location) {
                return [];
            }

            const locationName = (location.location || "").toLowerCase();
            const fees = [];

            // Back Name Fee (for Back location)
            if (locationName === "back") {
                // Try to get from runChargeDetails first
                if (
                    this.runChargeDetails &&
                    Array.isArray(this.runChargeDetails)
                ) {
                    const backNameFee = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name.toLowerCase().includes("back name fee") &&
                            !d.name.toLowerCase().includes("decor up")
                    );
                    if (backNameFee && parseFloat(backNameFee.value || 0) > 0) {
                        // Use unitPrice and quantity from runChargeDetails (backNameCount is already in quantity)
                        const perItemFee = parseFloat(
                            backNameFee.unitPrice || 0
                        );
                        const quantity = parseFloat(backNameFee.quantity || 0); // This is backNameCount from ItemDetails
                        const total = parseFloat(backNameFee.value || 0);
                        fees.push({
                            name: backNameFee.name,
                            perItemFee: perItemFee,
                            quantity: quantity,
                            total: total
                        });
                    }
                }

                // Note: Fallback calculation removed - fees should always come from runChargeDetails
                // which is calculated based on actual names/numbers count, not color count
                // If runChargeDetails doesn't have the fee, it means there are no names/numbers added

                // Back Number Fee (for Back location)
                if (
                    this.runChargeDetails &&
                    Array.isArray(this.runChargeDetails)
                ) {
                    const backNumberFee = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name.toLowerCase().includes("back number fee") &&
                            !d.name.toLowerCase().includes("decor up")
                    );
                    if (
                        backNumberFee &&
                        parseFloat(backNumberFee.value || 0) > 0
                    ) {
                        // Use unitPrice and quantity from runChargeDetails (backNumberCount is already in quantity)
                        const perItemFee = parseFloat(
                            backNumberFee.unitPrice || 0
                        );
                        const quantity = parseFloat(
                            backNumberFee.quantity || 0
                        ); // This is backNumberCount from ItemDetails
                        const total = parseFloat(backNumberFee.value || 0);
                        fees.push({
                            name: backNumberFee.name,
                            perItemFee: perItemFee,
                            quantity: quantity,
                            total: total
                        });
                    }
                }

                // Note: Fallback calculation removed - fees should always come from runChargeDetails
                // which is calculated based on actual names/numbers count, not color count
                // If runChargeDetails doesn't have the fee, it means there are no names/numbers added

                // Back Name Decor Up Fee (still from runChargeDetails)
                if (
                    this.runChargeDetails &&
                    Array.isArray(this.runChargeDetails)
                ) {
                    const backNameDecorUp = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name
                                .toLowerCase()
                                .includes("back name decor up fee")
                    );
                    if (
                        backNameDecorUp &&
                        parseFloat(backNameDecorUp.value || 0) > 0
                    ) {
                        // Get the quantity from Back Name Fee (not product quantity)
                        const backNameFee = this.runChargeDetails.find(
                            (d) =>
                                d.name &&
                                d.name
                                    .toLowerCase()
                                    .includes("back name fee") &&
                                !d.name.toLowerCase().includes("decor up")
                        );
                        const quantity = backNameFee
                            ? parseFloat(backNameFee.quantity || 0)
                            : this.quantity;

                        // Calculate per-item fee: if unitPrice exists use it, otherwise divide total value by quantity
                        let perItemFee = 0;
                        if (backNameDecorUp.unitPrice) {
                            perItemFee = parseFloat(
                                backNameDecorUp.unitPrice || 0
                            );
                        } else if (quantity > 0) {
                            // Value is total, divide by quantity to get per-item price
                            perItemFee =
                                parseFloat(backNameDecorUp.value || 0) /
                                quantity;
                        } else {
                            perItemFee = parseFloat(backNameDecorUp.value || 0);
                        }
                        const total = perItemFee * quantity;
                        fees.push({
                            name: backNameDecorUp.name,
                            perItemFee: perItemFee,
                            quantity: quantity,
                            total: total
                        });
                    }
                }

                // Back Number Decor Up Fee (still from runChargeDetails)
                if (
                    this.runChargeDetails &&
                    Array.isArray(this.runChargeDetails)
                ) {
                    const backNumberDecorUp = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name
                                .toLowerCase()
                                .includes("back number decor up fee")
                    );
                    if (
                        backNumberDecorUp &&
                        parseFloat(backNumberDecorUp.value || 0) > 0
                    ) {
                        // Get the quantity from Back Number Fee (not product quantity)
                        const backNumberFee = this.runChargeDetails.find(
                            (d) =>
                                d.name &&
                                d.name
                                    .toLowerCase()
                                    .includes("back number fee") &&
                                !d.name.toLowerCase().includes("decor up")
                        );
                        const quantity = backNumberFee
                            ? parseFloat(backNumberFee.quantity || 0)
                            : this.quantity;

                        // Calculate per-item fee: if unitPrice exists use it, otherwise divide total value by quantity
                        let perItemFee = 0;
                        if (backNumberDecorUp.unitPrice) {
                            perItemFee = parseFloat(
                                backNumberDecorUp.unitPrice || 0
                            );
                        } else if (quantity > 0) {
                            // Value is total, divide by quantity to get per-item price
                            perItemFee =
                                parseFloat(backNumberDecorUp.value || 0) /
                                quantity;
                        } else {
                            perItemFee = parseFloat(
                                backNumberDecorUp.value || 0
                            );
                        }
                        const total = perItemFee * quantity;
                        fees.push({
                            name: backNumberDecorUp.name,
                            perItemFee: perItemFee,
                            quantity: quantity,
                            total: total
                        });
                    }
                }
            }

            return fees;
        },
        // Get other run charge details (only non-location-specific fees)
        getOtherRunChargeDetails() {
            if (
                !this.runChargeDetails ||
                !Array.isArray(this.runChargeDetails)
            ) {
                return [];
            }

            // Filter out location-related details that are already shown in the table
            // Note: Custom Color Ink Fee and Custom Color Thread Fee are shown location-wise in table,
            // but we need to check if they're already accounted for in location-wise calculations
            const excludedNames = [
                "Location(s) Run Charge",
                "Front Number Fee",
                "Back Name Fee",
                "Back Number Fee",
                "Front Number Decor Up Fee",
                "Back Name Decor Up Fee",
                "Back Number Decor Up Fee"
            ];

            // Check if any location has color upcharge
            let hasLocationWiseUpcharge = false;
            if (this.imprintLocations && this.imprintLocations.length > 0) {
                const firstLocationUpcharge =
                    this.getFirstLocationColorUpcharge();
                if (firstLocationUpcharge > 0) {
                    hasLocationWiseUpcharge = true;
                }
                const additionalLocations = this.getAdditionalLocations();
                if (additionalLocations.length > 0) {
                    additionalLocations.forEach((location) => {
                        if (
                            this.getLocationColorUpcharge(location.location) > 0
                        ) {
                            hasLocationWiseUpcharge = true;
                        }
                    });
                }
            }

            return this.runChargeDetails
                .filter((detail) => {
                    if (!detail || !detail.name) return false;

                    // Exclude Custom Color Ink/Thread Fee - it's shown separately as per-order fee
                    const isMixFee =
                        detail.name
                            .toLowerCase()
                            .includes("custom color ink fee") ||
                        detail.name
                            .toLowerCase()
                            .includes("custom color thread fee") ||
                        detail.name
                            .toLowerCase()
                            .includes("color ink mix fee") ||
                        detail.name
                            .toLowerCase()
                            .includes("color thread mix fee");
                    if (isMixFee) {
                        return false; // Exclude - shown separately after additional locations
                    }

                    // Exclude Specialty Ink Fee if it's already shown location-wise
                    const isColorUpcharge = detail.name
                        .toLowerCase()
                        .includes("specialty ink fee");
                    if (isColorUpcharge && hasLocationWiseUpcharge) {
                        return false; // Exclude combined total if location-wise upcharges exist
                    }

                    // Check if this detail name is excluded
                    const isExcluded = excludedNames.some((excluded) =>
                        detail.name
                            .toLowerCase()
                            .includes(excluded.toLowerCase())
                    );
                    return !isExcluded;
                })
                .map((detail) => {
                    // Determine if this fee is per item based on the name
                    const perItemFees = ["specialty ink fee"];
                    const isPerItem = perItemFees.some((fee) =>
                        detail.name.toLowerCase().includes(fee)
                    );

                    const totalValue = parseFloat(detail.value || 0);
                    return {
                        name: detail.name,
                        value: totalValue,
                        perItem: isPerItem,
                        perItemFee: isPerItem
                            ? totalValue / this.quantity
                            : totalValue
                    };
                });
        },
        // Get remaining stitches over 10,000
        getRemainingStitches() {
            return this.stitchCount > 10000 ? this.stitchCount - 10000 : 0;
        },
        // Get stitch cost
        getStitchCost() {
            if (this.stitchCount <= 10000) return 0;
            const stitchCost = this.getServiceMasterData("emb_1000_over_10k");
            const remainingStitches = this.stitchCount - 10000;
            return (
                parseInt(remainingStitches / 1000) * stitchCost * this.quantity
            );
        },
        // Get color upcharge for first location
        getFirstLocationColorUpcharge() {
            if (!this.imprintLocations || this.imprintLocations.length === 0) {
                return 0;
            }
            // Find first selected location
            const firstLocation = this.imprintLocations.find(
                (loc) => loc.is_selected
            );
            if (!firstLocation) {
                // Fallback to first location in array
                return this.getLocationColorUpcharge(this.imprintLocations[0]);
            }
            return this.getLocationColorUpcharge(firstLocation);
        },
        // Get color upcharge for a specific location
        getLocationColorUpcharge(location) {
            if (
                !location ||
                !this.vendorColors ||
                !Array.isArray(this.vendorColors)
            ) {
                return 0;
            }

            // Check if colors array exists
            const colorsArray = location.colors || [];
            if (!Array.isArray(colorsArray) || colorsArray.length === 0) {
                return 0;
            }

            let totalUpcharge = 0;
            colorsArray.forEach((colorObj) => {
                // Check if color object exists and has the right structure
                if (!colorObj || !colorObj.color) return;

                const color = colorObj.color;
                const colorValue = color.color_value || color.hex || "";
                if (!colorValue) return;

                // Find matching vendor color
                const vendorColor = this.vendorColors.find(
                    (vc) =>
                        vc.color_value &&
                        vc.color_value.toLowerCase() ===
                            colorValue.toLowerCase()
                );

                if (vendorColor && vendorColor.decor_upcharge) {
                    const upcharge = parseFloat(
                        vendorColor.decor_upcharge || 0
                    );
                    if (upcharge > 0) {
                        totalUpcharge += upcharge;
                    }
                }
            });

            return parseFloat(totalUpcharge.toFixed(2));
        },
        // Get order-level mix fee data (one-time, per order)
        getOrderMixFeeData() {
            if (
                !this.runChargeDetails ||
                !Array.isArray(this.runChargeDetails)
            ) {
                return null;
            }

            // Find Custom Color Ink Fee or Custom Color Thread Fee in runChargeDetails
            const mixFeeDetail = this.runChargeDetails.find((detail) => {
                if (!detail || !detail.name) return false;
                const nameLower = detail.name.toLowerCase();
                return (
                    (nameLower.includes("custom color ink fee") ||
                        nameLower.includes("custom color thread fee") ||
                        nameLower.includes("color ink mix fee") ||
                        nameLower.includes("color thread mix fee")) &&
                    !nameLower.includes("decor up")
                );
            });

            if (!mixFeeDetail) {
                return null;
            }

            // Get mix color count and unit price from the detail object
            const mixColorCount = mixFeeDetail.mixColorCount || 0;
            const unitPrice = mixFeeDetail.unitPrice || 0;
            const total = parseFloat(mixFeeDetail.value || 0);

            if (total <= 0) {
                return null;
            }

            return {
                mixColorCount: mixColorCount,
                unitPrice: unitPrice,
                total: total
            };
        },
        // Get color value (hex) from combination object
        getColorValue(combination) {
            if (!combination) return "#cccccc";
            // Try different possible property names: color_value, hex, hash
            let colorValue =
                combination.color_value ||
                combination.hex ||
                combination.hash ||
                (combination.color && combination.color.color_value) ||
                (combination.color && combination.color.hex) ||
                (combination.color && combination.color.hash) ||
                "";

            // Ensure color value has # prefix
            if (colorValue && !colorValue.startsWith("#")) {
                colorValue = "#" + colorValue;
            }

            return colorValue || "#cccccc";
        },
        // Get color name from combination object
        getColorName(combination) {
            if (!combination) return "N/A";
            // Try different possible property names
            return (
                combination.color_name ||
                combination.name ||
                (combination.color && combination.color.color_name) ||
                (combination.color && combination.color.name) ||
                "Color"
            );
        },
        // Get unit price for included services (show_service = false) for base fee
        getIncludedServicesUnitPrice() {
            const baseFeeServices = this.getServicesGroupedByRollupFee(1);
            let includedServicesUnitPrice = 0;

            baseFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    const show =
                        service.show_service !== undefined
                            ? service.show_service
                            : true;
                    if (show === false) {
                        // Get unit price directly (retail_cost), don't divide by quantity
                        const serviceUnitPrice =
                            this.getServiceUnitPrice(service);
                        includedServicesUnitPrice += serviceUnitPrice;
                    }
                });
            });

            return parseFloat(includedServicesUnitPrice.toFixed(4));
        },
        // Get unit price for a color-size combination (including included services)
        getColorSizeUnitPrice(combination) {
            if (!combination) return 0;
            let baseUnitPrice = 0;

            // Use cost or total_cost divided by quantity
            if (combination.cost !== undefined) {
                baseUnitPrice = parseFloat(combination.cost) || 0;
            } else if (combination.total_cost && combination.quantity) {
                baseUnitPrice =
                    parseFloat(combination.total_cost) /
                        parseInt(combination.quantity) || 0;
            } else if (this.baseCost > 0 && this.quantity > 0) {
                // Fallback: calculate from baseCost and total quantity
                baseUnitPrice = parseFloat(
                    (this.baseCost / this.quantity).toFixed(2)
                );
            }

            // Add included services unit price
            const includedServicesUnitPrice =
                this.getIncludedServicesUnitPrice();
            return parseFloat(
                (baseUnitPrice + includedServicesUnitPrice).toFixed(2)
            );
        },
        // Get total price for a color-size combination
        getColorSizeTotalPrice(combination) {
            if (!combination || !combination.quantity) return 0;
            const unitPrice = this.getColorSizeUnitPrice(combination);
            const qty = parseInt(combination.quantity) || 0;
            return parseFloat((unitPrice * qty).toFixed(2));
        }
    },

    mounted() {
        document.addEventListener("click", this.closeTooltipOnClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.closeTooltipOnClickOutside);
    }
};
</script>
<style scoped>
.cost-breakdown-container {
    width: 100%;
    max-width: 380px;
    border: 1px solid #e0e0e0;
    padding: 10px 15px;
    border-radius: 8px;
    background-color: #f9f9f9;
}

@media (max-width: 767px) {
    .cost-breakdown-container {
        margin-top: 1.5rem;
        max-width: 100%;
    }
}

.ml-auto {
    margin-left: auto;
}

.cost-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
}

.cost-label {
    font-size: 16px;
    color: #555;
}

.cost-value {
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.discount-row .cost-label,
.discount-row .cost-value {
    color: #d9534f;
}

.cost-separator {
    margin: 8px 0;
    border-top: 1px solid #ddd;
}

.total-row {
    padding-top: 8px;
}

.cost-label-total,
.cost-value-total {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 0;
}

.tooltip-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
}

.info-icon-svg {
    width: 16px;
    height: 16px;
    margin-left: 5px;
    color: #888;
    transition: color 0.2s;
}

.tooltip-wrapper:hover .info-icon-svg {
    color: #333;
}

/* --- TOOLTIP STYLES --- */
.tooltip-content {
    visibility: hidden;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #fff;
    color: hsla(0, 0%, 7%, 1);
    padding: 0px;
    border-radius: 6px;
    z-index: 10;
    min-width: 392px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    margin-bottom: 10px;
    border: 1px solid #ddd;
    opacity: 0;
    transition: opacity 0.3s;
    white-space: normal;
    font-size: 14px;
    z-index: 9999;
}

.tooltip-content::after {
    content: none;
}

.tooltip-content.is-visible {
    visibility: visible;
    opacity: 1;
}

.tooltip-header {
    padding: 9px 16px;
    background: hsla(204, 63%, 94%, 1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
}

.tooltip-header h4 {
    font-weight: 600;
    font-size: 14px;
    line-height: 14px;
    color: hsla(209, 72%, 41%, 1);
    margin-bottom: 0px;
}

.tooltip-body {
    white-space: normal;
    padding: 16px;
}

.tooltip-body ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.tooltip-body li {
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    font-size: 14px;
}

.tooltip-body li:last-child {
    margin-bottom: 0;
}
.tooltip-body li.gray-out {
    color: #999;
}

.tooltip-body li.gray-out span:last-child {
    color: #999;
}

/* Table styles matching ServiceCalculator */
.base-fee-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
    font-size: 12px;
}

.base-fee-table thead {
    background-color: #f5f5f5;
}

.base-fee-table th {
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
}

.base-fee-table th:nth-child(2),
.base-fee-table th:nth-child(3),
.base-fee-table th:nth-child(4) {
    text-align: right;
}

.base-fee-table td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #eee;
}

.base-fee-table td:nth-child(2),
.base-fee-table td:nth-child(3),
.base-fee-table td:nth-child(4) {
    text-align: right;
}

.base-fee-table tbody tr.disable_data {
    color: #666;
}

.base-fee-table tbody tr.total-row {
    border-top: 2px solid #ddd;
    background-color: #f9f9f9;
    font-weight: bold;
}

.base-fee-table tbody tr.total-row td {
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: none;
}

.color-box-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
}

.color-box {
    width: 20px;
    height: 20px;
    border: 1px solid #ddd;
    border-radius: 3px;
    cursor: pointer;
    display: inline-block;
}

.color-box:hover {
    border-color: #333;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.product-base-price-row {
    font-weight: bold;
}
</style>